import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';

interface UserProfile {
  id: string;
  user_type: 'maker' | 'seeker' | 'node' | null;
  display_name: string | null;
  prefecture: string | null;
  is_premium: boolean;
  plan_type: string;
  is_admin: boolean;
  favorite_genres?: string[] | null;
  favorite_sound_elements?: string[] | null;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [planType, setPlanType] = useState<'free' | 'maker' | 'seeker' | 'deepcore' | 'premium' | 'node'>('free');
  const [isAdmin, setIsAdmin] = useState(false);
  const initializedRef = useRef(false);

  const resetAuthState = useCallback(() => {
    setUser(null);
    setProfile(null);
    setIsPremium(false);
    setPlanType('free');
    setIsAdmin(false);
  }, []);

  const fetchPremiumStatus = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, is_premium, plan_type, is_admin, user_type, display_name, prefecture, favorite_genres, favorite_sound_elements')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('プロフィール取得エラー:', error);
        return;
      }

      if (data) {
        setIsPremium(data.is_premium || false);
        setPlanType(data.plan_type || 'free');
        setIsAdmin(data.is_admin || false);
        setProfile({
          id: data.id,
          user_type: data.user_type || null,
          display_name: data.display_name || null,
          prefecture: data.prefecture || null,
          is_premium: data.is_premium || false,
          plan_type: data.plan_type || 'free',
          is_admin: data.is_admin || false,
          favorite_genres: data.favorite_genres || null,
          favorite_sound_elements: data.favorite_sound_elements || null,
        });
      }
    } catch (err) {
      console.error('プロフィール取得に失敗:', err);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!mounted) return;
        
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        if (currentUser) {
          await fetchPremiumStatus(currentUser.id);
        }
      } catch (err) {
        console.error('Auth Init Error:', err);
      } finally {
        if (mounted) {
          initializedRef.current = true;
          setLoading(false);
        }
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!initializedRef.current || !mounted) return;

      if (event === 'SIGNED_OUT') {
        resetAuthState();
        setLoading(false);
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setUser(session?.user ?? null);
        fetchPremiumStatus(session?.user?.id ?? '');
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchPremiumStatus, resetAuthState]);

  return {
    user,
    profile,
    loading,
    isPremium,
    planType,
    isAdmin,
    refreshPremiumStatus: () => user ? fetchPremiumStatus(user.id) : Promise.resolve(),
    signOut: () => supabase.auth.signOut(),
  };
}
